import React, { useEffect, useState } from "react";
import { useAuth } from "../Components/Auth/useAuth";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import { useSideNavMenu } from "../hooks/useSideNavMenu";

const LARGE_SCREEN_QUERY = "(min-width: 1280px)";
const MOBILE_SCREEN_QUERY = "(max-width: 767px)";

function hasChildren(item) {
  const { items: children } = item;

  if (children === undefined) {
    return false;
  }

  if (children.constructor !== Array) {
    return false;
  }

  if (children.length === 0) {
    return false;
  }

  return true;
}

export default function SideNav() {
  const navigate = useNavigate();
  const { user, activePortal } = useAuth();
  const sideNav = useSideNavMenu(user, activePortal);
  const [isLargeScreen, setIsLargeScreen] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(LARGE_SCREEN_QUERY).matches
      : false
  );
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(MOBILE_SCREEN_QUERY).matches
      : false
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const collapsed = isMobile ? false : isLargeScreen ? false : !isHovered;
  const sideNavWidth = isMobile ? (mobileOpen ? 280 : 0) : collapsed ? 62 : 325;

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(LARGE_SCREEN_QUERY);
    const mobileQuery = window.matchMedia(MOBILE_SCREEN_QUERY);
    const handleMediaChange = (event) => {
      setIsLargeScreen(event.matches);
      if (event.matches) {
        setIsHovered(false);
      }
    };

    setIsLargeScreen(mediaQuery.matches);
    setIsMobile(mobileQuery.matches);

    const handleMobileChange = (event) => {
      setIsMobile(event.matches);
      setMobileOpen(false);
    };
    const toggleNavigation = () => setMobileOpen((current) => !current);
    window.addEventListener("ftmc:toggle-navigation", toggleNavigation);

    if (mediaQuery.addEventListener && mobileQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleMediaChange);
      mobileQuery.addEventListener("change", handleMobileChange);
      return () => {
        mediaQuery.removeEventListener("change", handleMediaChange);
        mobileQuery.removeEventListener("change", handleMobileChange);
        window.removeEventListener("ftmc:toggle-navigation", toggleNavigation);
      };
    }

    mediaQuery.addListener(handleMediaChange);
    mobileQuery.addListener(handleMobileChange);
    return () => {
      mediaQuery.removeListener(handleMediaChange);
      mobileQuery.removeListener(handleMobileChange);
      window.removeEventListener("ftmc:toggle-navigation", toggleNavigation);
    };
  }, []);

  // if single level can have either onClick OR to
  // if Multi Level it can have both an OnCLcik AND to
  // if Multi level and doesn't have a TO it means it is ROOT

  const MenuItem = ({ item }) => {
    const Component = hasChildren(item) ? MultiLevel : SingleLevel;
    return <Component item={item} />;
  };

  const rootItemSx = {
    minHeight: 56,
    px: 2,
    py: 0,
  };

  const SingleLevel = ({ item }) => {
    const borderStyle =
      "borderColor" in item
        ? {
            borderBottom: `1px solid ${item.borderColor}`,
            "&:hover": {
              borderBottom: `2px solid ${item.borderColor}`,
            },
          }
        : {};
    return (
      <Button
        sx={{
          textTransform: "capitalize",
          ...borderStyle,
        }}
      >
        {" "}
        <ListItem
          sx={rootItemSx}
          onClick={() => {
            "onClick" in item ? item.onClick() : navigate(item.to);
            if (isMobile) setMobileOpen(false);
          }}
        >
          <ListItemIcon>{item.icon}</ListItemIcon>
          {!collapsed && (
            <ListItemText className="text-white" primary={item.title} />
          )}
        </ListItem>
      </Button>
    );
  };

  const MultiLevel = ({ item }) => {
    const { items: children } = item;
    const [open, setOpen] = useState(false);
    const isFieldLibraryRoot = item.title === "Field Library";

    const handleClick = () => {
      setOpen((prev) => !prev);
    };
    const borderStyle =
      "borderColor" in item
        ? {
            borderBottom: `1px solid ${item.borderColor}`,
            "&:hover": {
              borderBottom: `2px solid ${item.borderColor}`,
            },
          }
        : {};
    return (
      <div
        className="w-full"
        onMouseLeave={() => {
          if (isFieldLibraryRoot) {
            setOpen(false);
          }
        }}
      >
        <Button
          sx={{
            textTransform: "capitalize",
            color: "#FFF",
            width: "100%",
            ...borderStyle,
          }}
        >
          <ListItem className="w-full" sx={rootItemSx}>
            <ListItemIcon
              sx={{ minWidth: 40, color: "inherit" }}
              onClick={() => {
                "to" in item ? navigate(item.to) : handleClick();
              }}
            >
              {item.icon}
            </ListItemIcon>

            {!collapsed && (
              <ListItemText
                sx={{ minWidth: 0, pr: 1 }}
                onClick={() => {
                  "to" in item ? navigate(item.to) : handleClick();
                }}
                primary={item.title}
              />
            )}
            {!collapsed ? (
              open ? (
                <ExpandLessIcon
                  className="shrink-0 text-white"
                  onClick={handleClick}
                />
              ) : (
                <ExpandMoreIcon
                  className="shrink-0 text-white"
                  onClick={handleClick}
                />
              )
            ) : (
              ""
            )}
          </ListItem>
        </Button>
        {!collapsed && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List
              component="div"
              className={isFieldLibraryRoot ? "overflow-y-auto overflow-x-hidden" : ""}
              style={{
                paddingLeft: "25px",
                maxHeight: isFieldLibraryRoot ? "calc(100vh - 280px)" : undefined,
              }}
            >
              {children.map((child, key) => (
                <MenuItem key={key} item={child} />
              ))}
            </List>
          </Collapse>
        )}
      </div>
    );
  };

  return (
    <>
      {isMobile && mobileOpen ? (
        <button
          aria-label="Close navigation"
          className="fixed inset-0 top-16 z-30 bg-slate-950/30"
          onClick={() => setMobileOpen(false)}
          type="button"
        />
      ) : null}
      <div
      className={`flex h-full min-h-0 shrink-0 flex-col overflow-hidden bg-[rgb(36,39,41)] transition-[width] duration-200 ${
        isMobile ? "fixed bottom-0 left-0 top-16 z-40 shadow-2xl" : "relative"
      }`}
      style={{
        width: `${sideNavWidth}px`,
        minWidth: `${sideNavWidth}px`,
        maxWidth: `${sideNavWidth}px`,
      }}
      onMouseEnter={() => {
        if (!isLargeScreen && !isMobile) {
          setIsHovered(true);
        }
      }}
      onMouseLeave={() => {
        if (!isLargeScreen && !isMobile) {
          setIsHovered(false);
        }
      }}
      onFocus={() => {
        if (!isLargeScreen && !isMobile) {
          setIsHovered(true);
        }
      }}
      onBlur={(event) => {
        if (!isLargeScreen && !isMobile && !event.currentTarget.contains(event.relatedTarget)) {
          setIsHovered(false);
        }
      }}
    >
      <Collapse
        className="h-full min-h-0"
        style={{ height: "100%" }}
        orientation="horizontal"
        in={isMobile ? mobileOpen : !collapsed}
        collapsedSize={isMobile ? 0 : 62}
      >
        <ul
          className="flex h-full min-h-0 flex-col overflow-y-auto overflow-x-hidden pb-6"
          style={{ width: isMobile ? "280px" : "325px", minWidth: isMobile ? "280px" : "325px" }}
        >
          {sideNav.menu.map((item, key) => (
            <MenuItem key={key} item={item} />
          ))}
        </ul>
      </Collapse>
      </div>
    </>
  );
}
// export default MultiLevel;
